import { Component, OnInit, signal, computed, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';
import {
  SettingsService,
  AVAILABLE_WALLPAPERS,
  AVAILABLE_LANGUAGES,
  AUTO_LOCK_OPTIONS,
  DesktopWallpapers,
} from './settings.service';
import { LockScreenService } from '../lockScreen/lock-screen.service';
import { DesktopData } from '../header/header.service';
import { AuthService, User } from '../services/auth.services';

// Tab configuration type
interface SettingsTab {
  id: string;
  label: string;
  icon: string;
  description: string;
}

// System info type
interface SystemInfo {
  appVersion: string;
  buildNumber: string;
  platform: string;
  userAgent: string;
  screenResolution: string;
  language: string;
  timezone: string;
  memoryUsage: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class SettingsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly desktops = DesktopData();

  // Active desktop for wallpaper selection (uses desktop ref: desktopOne, desktopTwo, etc.)
  readonly activeWallpaperDesktop = signal<keyof DesktopWallpapers>('desktopOne');

  // Computed: current wallpaper for the selected desktop
  readonly currentDesktopWallpaper = computed(() => {
    return this.settingsService.getDesktopWallpaper(this.activeWallpaperDesktop());
  });

  // Tab configuration
  readonly tabs: SettingsTab[] = [
    { id: 'display', label: 'Display', icon: 'fa-palette', description: 'Theme, wallpaper, and visual settings' },
    { id: 'desktop', label: 'Desktop', icon: 'fa-desktop', description: 'Desktop behavior and window management' },
    { id: 'privacy', label: 'Privacy & Security', icon: 'fa-shield-halved', description: 'Lock screen and data protection' },
    { id: 'notifications', label: 'Notifications', icon: 'fa-bell', description: 'Alert preferences and sounds' },
    { id: 'accessibility', label: 'Accessibility', icon: 'fa-universal-access', description: 'Visual aids and navigation' },
    { id: 'language', label: 'Language & Region', icon: 'fa-globe', description: 'Language and date/time formats' },
    { id: 'about', label: 'About', icon: 'fa-info-circle', description: 'App information and system details' },
  ];

  // Active tab signal
  readonly activeTab = signal('display');

  // Data constants
  readonly wallpapers = AVAILABLE_WALLPAPERS;
  readonly languages = AVAILABLE_LANGUAGES;
  readonly autoLockOptions = AUTO_LOCK_OPTIONS;

  // Date format options
  readonly dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (DE)' },
  ];

  // Font size options
  readonly fontSizes = [
    { value: 'small', label: 'Small', size: '14px' },
    { value: 'medium', label: 'Medium', size: '16px' },
    { value: 'large', label: 'Large', size: '18px' },
  ];

  // User profile
  userProfile: any = {};

  // System info
  readonly systemInfo = signal<SystemInfo>({
    appVersion: '2.1.0',
    buildNumber: '2024.12.001',
    platform: '',
    userAgent: '',
    screenResolution: '',
    language: '',
    timezone: '',
    memoryUsage: '',
  });

  // Storage info
  readonly storageUsed = signal('0 MB');
  readonly storageTotal = signal('5 MB');
  readonly storagePercentage = computed(() => {
    const used = parseFloat(this.storageUsed().replace(' MB', ''));
    const total = parseFloat(this.storageTotal().replace(' MB', ''));
    return Math.round((used / total) * 100);
  });

  // Current date
  readonly now = computed(() => new Date());


  constructor(
    public settingsService: SettingsService,
    public auth: AuthService,
    private lockScreenService: LockScreenService
  ) { }

  ngOnInit(): void {
    this.loadUserProfile();
    this.loadSystemInfo();
    this.calculateStorageUsage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Tab navigation
  setActiveTab(tabId: string): void {
    this.activeTab.set(tabId);
  }

  // Load user profile from Auth0
  private loadUserProfile(): void {
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User | null) => {
        if (user) {
          this.userProfile = user;
        }
      });
  }

  // Load system information
  private loadSystemInfo(): void {
    const nav = navigator as any;

    this.systemInfo.set({
      appVersion: '2.1.0',
      buildNumber: '2024.12.001',
      platform: nav.platform || 'Unknown',
      userAgent: nav.userAgent || 'Unknown',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: nav.language || 'en-US',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      memoryUsage: nav.deviceMemory ? `${nav.deviceMemory} GB` : 'N/A',
    });
  }

  // Calculate localStorage usage
  private calculateStorageUsage(): void {
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length * 2; // UTF-16 = 2 bytes per char
      }
    }
    const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
    this.storageUsed.set(`${sizeInMB} MB`);
  }

  // Display settings handlers
  setTheme(theme: 'dark' | 'light' | 'system'): void {
    this.settingsService.updateDisplaySettings({ theme });
  }

  setWallpaper(wallpaperUrl: string): void {
    // Update wallpaper for the currently selected desktop
    this.settingsService.updateDesktopWallpaper(this.activeWallpaperDesktop(), wallpaperUrl);
  }

  setActiveWallpaperDesktop(desktopRef: keyof DesktopWallpapers): void {
    this.activeWallpaperDesktop.set(desktopRef);
  }

  setFontSize(size: string | 'small' | 'medium' | 'large'): void {
    this.settingsService.updateDisplaySettings({ fontSize: size });
  }

  toggleAnimations(enabled: boolean): void {
    this.settingsService.updateDisplaySettings({ animationsEnabled: enabled });
  }

  toggleReducedMotion(enabled: boolean): void {
    this.settingsService.updateDisplaySettings({ reducedMotion: enabled });
  }

  // Desktop settings handlers
  setDefaultDesktop(desktop: number): void {
    this.settingsService.updateDesktopSettings({ defaultDesktop: desktop });
  }

  toggleDesktopPreviews(enabled: boolean): void {
    this.settingsService.updateDesktopSettings({ showDesktopPreviews: enabled });
  }

  toggleDoubleClickMaximize(enabled: boolean): void {
    this.settingsService.updateDesktopSettings({ doubleClickToMaximize: enabled });
  }

  toggleSnapToEdges(enabled: boolean): void {
    this.settingsService.updateDesktopSettings({ snapWindowsToEdges: enabled });
  }

  getDesktopLabel(ref: string): string {
    return this.settingsService.getDesktopLabel(ref);
  }

  getDesktopLabelValue(ref: string): string {
    const labels = this.currentSettings.desktop.desktopLabels;
    return (labels as Record<string, string>)?.[ref] ?? '';
  }

  setDesktopLabel(ref: string, value: string): void {
    const current = (this.currentSettings.desktop.desktopLabels ?? {}) as Record<string, string>;
    this.settingsService.updateDesktopSettings({
      desktopLabels: { ...current, [ref]: value }
    });
  }

  // Privacy settings handlers
  toggleAutoLock(enabled: boolean): void {
    this.settingsService.updatePrivacySettings({ autoLockEnabled: enabled });
  }

  setAutoLockTimeout(timeout: number): void {
    this.settingsService.updatePrivacySettings({ autoLockTimeout: timeout });
  }

  toggleLockScreenNotifications(enabled: boolean): void {
    this.settingsService.updatePrivacySettings({ showNotificationsOnLockScreen: enabled });
  }

  toggleRememberApps(enabled: boolean): void {
    this.settingsService.updatePrivacySettings({ rememberOpenApps: enabled });
  }

  toggleClearDataOnLogout(enabled: boolean): void {
    this.settingsService.updatePrivacySettings({ clearDataOnLogout: enabled });
  }

  lockNow(): void {
    this.lockScreenService.lock();
  }

  // Notification settings handlers
  toggleNotifications(enabled: boolean): void {
    this.settingsService.updateNotificationSettings({ enabled });
  }

  toggleNotificationSound(enabled: boolean): void {
    this.settingsService.updateNotificationSettings({ sound: enabled });
  }

  toggleBadgeCount(enabled: boolean): void {
    this.settingsService.updateNotificationSettings({ badgeCount: enabled });
  }

  togglePreviewText(enabled: boolean): void {
    this.settingsService.updateNotificationSettings({ previewText: enabled });
  }

  toggleDoNotDisturb(enabled: boolean): void {
    this.settingsService.updateNotificationSettings({ doNotDisturb: enabled });
  }

  setDoNotDisturbSchedule(start: string, end: string): void {
    this.settingsService.updateNotificationSettings({
      doNotDisturbStart: start,
      doNotDisturbEnd: end,
    });
  }

  // Accessibility settings handlers
  toggleHighContrast(enabled: boolean): void {
    this.settingsService.updateAccessibilitySettings({ highContrast: enabled });
  }

  toggleLargeText(enabled: boolean): void {
    this.settingsService.updateAccessibilitySettings({ largeText: enabled });
  }

  toggleScreenReader(enabled: boolean): void {
    this.settingsService.updateAccessibilitySettings({ screenReader: enabled });
  }

  toggleKeyboardNavigation(enabled: boolean): void {
    this.settingsService.updateAccessibilitySettings({ keyboardNavigation: enabled });
  }

  toggleFocusIndicators(enabled: boolean): void {
    this.settingsService.updateAccessibilitySettings({ focusIndicators: enabled });
  }

  // Language & Region handlers
  setLanguage(language: string): void {
    this.settingsService.updateLanguage(language);
  }

  setDateFormat(format: string): void {
    this.settingsService.updateDateFormat(format);
  }

  setTimeFormat(format: '12h' | '24h'): void {
    this.settingsService.updateTimeFormat(format);
  }

  // Save settings
  saveSettings(): void {
    this.settingsService.saveSettings();
    Swal.fire({
      title: 'Settings Saved',
      text: 'Your preferences have been saved successfully.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      background: '#1a1a2e',
      color: '#fff',
    });
  }

  // Reset settings
  async resetSettings(): Promise<void> {
    const result = await Swal.fire({
      title: 'Reset Settings?',
      text: 'This will restore all settings to their default values. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      background: '#1a1a2e',
      color: '#fff',
    });

    if (result.isConfirmed) {
      this.settingsService.resetToDefaults();
      this.settingsService.saveSettings();
      Swal.fire({
        title: 'Settings Reset',
        text: 'All settings have been restored to defaults.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        background: '#1a1a2e',
        color: '#fff',
      });
    }
  }

  // Export settings
  exportSettings(): void {
    const json = this.settingsService.exportSettings();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ews-settings-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

    Swal.fire({
      title: 'Settings Exported',
      text: 'Your settings have been downloaded as a JSON file.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      background: '#1a1a2e',
      color: '#fff',
    });
  }

  // Import settings
  async importSettings(): Promise<void> {
    const { value: file } = await Swal.fire({
      title: 'Import Settings',
      input: 'file',
      inputAttributes: {
        accept: '.json',
        'aria-label': 'Upload your settings file',
      },
      showCancelButton: true,
      confirmButtonText: 'Import',
      background: '#1a1a2e',
      color: '#fff',
    });

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const json = e.target?.result as string;
        const success = this.settingsService.importSettings(json);

        if (success) {
          this.settingsService.saveSettings();
          Swal.fire({
            title: 'Settings Imported',
            text: 'Your settings have been imported successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            background: '#1a1a2e',
            color: '#fff',
          });
        } else {
          Swal.fire({
            title: 'Import Failed',
            text: 'The file could not be parsed. Please ensure it is a valid settings file.',
            icon: 'error',
            background: '#1a1a2e',
            color: '#fff',
          });
        }
      };
      reader.readAsText(file);
    }
  }

  // Clear local storage
  async clearStorage(): Promise<void> {
    const result = await Swal.fire({
      title: 'Clear Storage?',
      text: 'This will clear all cached data including running apps state. Your settings will be preserved.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      background: '#1a1a2e',
      color: '#fff',
    });

    if (result.isConfirmed) {
      // Save current settings
      const currentSettings = this.settingsService.exportSettings();

      // Clear all localStorage
      localStorage.clear();

      // Restore settings
      this.settingsService.importSettings(currentSettings);
      this.settingsService.saveSettings();

      this.calculateStorageUsage();

      Swal.fire({
        title: 'Storage Cleared',
        text: 'All cached data has been cleared.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        background: '#1a1a2e',
        color: '#fff',
      });
    }
  }

  // Check for updates (mock)
  checkForUpdates(): void {
    Swal.fire({
      title: 'Checking for Updates...',
      text: 'Please wait while we check for the latest version.',
      allowOutsideClick: false,
      background: '#1a1a2e',
      color: '#fff',
      didOpen: () => {
        Swal.showLoading();
        setTimeout(() => {
          Swal.fire({
            title: 'You\'re up to date!',
            text: `EWS Platform v${this.systemInfo().appVersion} is the latest version.`,
            icon: 'success',
            background: '#1a1a2e',
            color: '#fff',
          });
        }, 1500);
      },
    });
  }

  // Helper to get current settings
  get currentSettings() {
    return this.settingsService.settings();
  }
}

